import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extension-placeholder';
import {
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    Popover,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import {
    FormatAlignCenter,
    FormatAlignLeft,
    FormatAlignRight,
    FormatBold,
    FormatClear,
    FormatColorText,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    FormatUnderlined,
    InsertEmoticon,
    Link as LinkIcon,
    StrikethroughS,
    Title,
} from '@mui/icons-material';
import { BotaoCor } from './SeletorCor';
import { SeletorEmoji } from './SeletorEmoji';
import { estilos } from '../config/estilos';

function Botao({ titulo, ativo, onClick, children, disabled }) {
    return (
        <Tooltip title={titulo}>
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    sx={{
                        borderRadius: 1,
                        color: ativo ? 'primary.main' : 'text.secondary',
                        bgcolor: ativo ? 'action.selected' : 'transparent',
                    }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

function BotaoLink({ editor }) {
    const [ancora, setAncora] = useState(null);
    const [url, setUrl] = useState('');

    const abrir = (evento) => {
        setUrl(editor.getAttributes('link').href ?? '');
        setAncora(evento.currentTarget);
    };

    const aplicar = () => {
        const valor = url.trim();

        if (!valor) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            const href = /^https?:\/\//i.test(valor) ? valor : `https://${valor}`;
            editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
        }

        setAncora(null);
    };

    return (
        <>
            <Botao titulo="Link" ativo={editor.isActive('link')} onClick={abrir}>
                <LinkIcon fontSize="small" />
            </Botao>

            <Popover open={!!ancora} anchorEl={ancora} onClose={() => setAncora(null)}>
                <Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
                    <TextField
                        size="small"
                        autoFocus
                        placeholder="https://..."
                        value={url}
                        onChange={(evento) => setUrl(evento.target.value)}
                        onKeyDown={(evento) => {
                            if (evento.key === 'Enter') {
                                evento.preventDefault();
                                aplicar();
                            }
                        }}
                    />
                    <Button variant="contained" size="small" onClick={aplicar}>
                        {url.trim() ? 'Aplicar' : 'Remover'}
                    </Button>
                </Stack>
            </Popover>
        </>
    );
}

export function EditorTexto({ value, onChange, placeholder = 'Escreva o conteúdo da notificação...', disabled = false }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [3] } }),
            TextStyle,
            Color,
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || '',
        editable: !disabled,
        onUpdate: ({ editor: atual }) => {
            onChange?.(atual.isEmpty ? '' : atual.getHTML());
        },
    });

    useEffect(() => {
        if (!editor) return;

        const atual = editor.isEmpty ? '' : editor.getHTML();

        if ((value || '') !== atual) {
            editor.commands.setContent(value || '', false);
        }
    }, [value, editor]);

    useEffect(() => {
        editor?.setEditable(!disabled);
    }, [disabled, editor]);

    if (!editor) return null;

    const cadeia = () => editor.chain().focus();
    const corAtual = editor.getAttributes('textStyle').color;

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden', opacity: disabled ? 0.6 : 1 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                flexWrap="wrap"
                useFlexGap
                sx={{ px: 0.75, py: 0.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}
            >
                <Botao titulo="Título" ativo={editor.isActive('heading', { level: 3 })} onClick={() => cadeia().toggleHeading({ level: 3 }).run()} disabled={disabled}>
                    <Title fontSize="small" />
                </Botao>
                <Botao titulo="Negrito" ativo={editor.isActive('bold')} onClick={() => cadeia().toggleBold().run()} disabled={disabled}>
                    <FormatBold fontSize="small" />
                </Botao>
                <Botao titulo="Itálico" ativo={editor.isActive('italic')} onClick={() => cadeia().toggleItalic().run()} disabled={disabled}>
                    <FormatItalic fontSize="small" />
                </Botao>
                <Botao titulo="Sublinhado" ativo={editor.isActive('underline')} onClick={() => cadeia().toggleUnderline().run()} disabled={disabled}>
                    <FormatUnderlined fontSize="small" />
                </Botao>
                <Botao titulo="Tachado" ativo={editor.isActive('strike')} onClick={() => cadeia().toggleStrike().run()} disabled={disabled}>
                    <StrikethroughS fontSize="small" />
                </Botao>

                <BotaoCor
                    valor={corAtual}
                    onSelecionar={(cor) => cadeia().setColor(cor).run()}
                    onLimpar={() => cadeia().unsetColor().run()}
                >
                    <Botao titulo="Cor do texto" disabled={disabled}>
                        <FormatColorText fontSize="small" sx={{ color: corAtual || 'inherit' }} />
                    </Botao>
                </BotaoCor>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Botao titulo="Lista" ativo={editor.isActive('bulletList')} onClick={() => cadeia().toggleBulletList().run()} disabled={disabled}>
                    <FormatListBulleted fontSize="small" />
                </Botao>
                <Botao titulo="Lista numerada" ativo={editor.isActive('orderedList')} onClick={() => cadeia().toggleOrderedList().run()} disabled={disabled}>
                    <FormatListNumbered fontSize="small" />
                </Botao>
                <Botao titulo="Citação" ativo={editor.isActive('blockquote')} onClick={() => cadeia().toggleBlockquote().run()} disabled={disabled}>
                    <FormatQuote fontSize="small" />
                </Botao>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Botao titulo="Alinhar à esquerda" ativo={editor.isActive({ textAlign: 'left' })} onClick={() => cadeia().setTextAlign('left').run()} disabled={disabled}>
                    <FormatAlignLeft fontSize="small" />
                </Botao>
                <Botao titulo="Centralizar" ativo={editor.isActive({ textAlign: 'center' })} onClick={() => cadeia().setTextAlign('center').run()} disabled={disabled}>
                    <FormatAlignCenter fontSize="small" />
                </Botao>
                <Botao titulo="Alinhar à direita" ativo={editor.isActive({ textAlign: 'right' })} onClick={() => cadeia().setTextAlign('right').run()} disabled={disabled}>
                    <FormatAlignRight fontSize="small" />
                </Botao>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <BotaoLink editor={editor} />

                <SeletorEmoji onSelecionar={(emoji) => cadeia().insertContent(emoji).run()}>
                    <Botao titulo="Emoji" disabled={disabled}>
                        <InsertEmoticon fontSize="small" />
                    </Botao>
                </SeletorEmoji>

                <Botao titulo="Limpar formatação" onClick={() => cadeia().unsetAllMarks().clearNodes().run()} disabled={disabled}>
                    <FormatClear fontSize="small" />
                </Botao>
            </Stack>

            <Box
                sx={{
                    ...estilos,
                    '& .ProseMirror': { minHeight: 160, p: 1.5, outline: 'none' },
                    '& .ProseMirror p.is-editor-empty:first-of-type::before': {
                        content: 'attr(data-placeholder)',
                        color: 'text.disabled',
                        float: 'left',
                        height: 0,
                        pointerEvents: 'none',
                    },
                }}
            >
                <EditorContent editor={editor} />
            </Box>
        </Paper>
    );
}